import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { RealtimeEnvelope } from '@/contracts/realtime-envelope-v1';
import { OUTBOX_FLUSH_MIN_INTERVAL_MS, OUTBOX_MAX_ENTRIES_PER_SCOPE } from '@/contracts/realtime-policy';
import router from '@/router';
import { messagesService, messageGroupsService } from '@/services/erpat';
import { getSessionEpoch } from '@/services/api/erpat-client';
import { errorMessage } from '@/services/api/http';
import { compareStreamSequence } from '@/services/api/models';
import { getSessionScope, isSelfActor, type SessionScope } from '@/services/session-scope';
import {
  ME,
  buildThreadIndex,
  conversationIdOf,
  envelopeAddressesOpenThread,
  parseThreadKey,
  threadKeyOfConversation,
  threadKeyOfEnvelope,
  type ChatMessage,
  type Conversation,
  type OpenThreadRef,
  type ThreadIndex,
  type ThreadKey,
} from '@/types/message';
import type { ReducerOutcome } from '@/types/notification';
import { toMillis } from '@/utils/format';
import {
  clearOutboxForSession as clearOutboxStorage,
  computeRequestHash,
  createOutboxEntry,
  isAlreadySentError,
  isRecipientUnavailableError,
  isRejectedError,
  isStaleAttachmentsError,
  listOutboxScopeKeys,
  loadOutbox,
  normalizeMessageText,
  outboxScopeKey,
  outboxSessionPrefix,
  OutboxFullError,
  persistOutbox,
  threadKeyOfScope,
  type OutboxAttachment,
  type OutboxMessage,
  type OutboxReason,
  type OutboxScopeKey,
} from '@/utils/message-outbox';

/**
 * Messaging state shared between the Messages tab list, the conversation
 * thread pages, and the bottom-tab unread badge — and, since Phase 6, the
 * per-thread reducer the realtime service feeds (phase6-contract.md §3.3).
 *
 * `conversations` keeps its shape and public surface: the REST list rows,
 * newest first. A parallel {@link threadState} map keyed by the unified thread
 * identity ({@link ThreadKey}) holds what realtime events and local reads have
 * changed since the list was fetched, and is PROJECTED onto the matching rows
 * — the pages keep reading `conversation.unread` / `updatedAt` as before.
 *
 * ONE badge rule: a direct thread is boolean (`unread ∈ {0, 1}` — the REST row
 * only knows "the latest message is unread"), a group counts. The badge
 * ({@link unreadTotal}) is the sum of the projected per-thread `unread`, so it
 * always equals the per-row badges the list shows. The server's
 * `unread_counts.messages` is NEVER adopted as the badge: a mismatch with the
 * projected total triggers a list refresh instead (today's rule).
 *
 * A message read reaches this store as a `notification.read` whose
 * `notification.category` is `message` (the server never emits
 * `message.read` — DEC-028). Such an event carries no thread identity of its
 * own, so the thread is remembered from the `message.created` /
 * `notification.created` that introduced the same notification id; without
 * that memory the event is `'unresolved'` and the service refreshes the list.
 *
 * Mirrors `useAsyncData`'s load/abort behaviour, but lives in a store (not a
 * component) so the count survives tab navigation and is reachable from the
 * always-mounted tab shell.
 */

/** What realtime and local reads changed for one thread since the last list fetch. */
export interface ThreadState {
  unread: number;
  /** Epoch ms of the last activity the store saw (the row's `updatedAt` is projected from it). */
  updatedAt: number;
}

/** The store's own memory of which thread a notification id belongs to (bounded). */
const NOTIFICATION_THREAD_MEMO_MAX = 500;

/** Envelope types that change a thread's unread state. */
const STATE_TYPES: ReadonlySet<string> = new Set(['notification.read', 'notification.dismissed']);

/**
 * The outbox `delivered` tick (phase6-contract.md §3.4 "ownership"): set once
 * per delivered entry. The open thread page watches it, refreshes its thread so
 * the server copy is on screen, then calls `dropDelivered()` — which is when
 * the optimistic bubble drops. `messageId` is '' when the server never named
 * it (a 409: the key is persisted, the message exists, the id is unknown).
 */
export interface OutboxDeliveredTick {
  scope: OutboxScopeKey;
  localId: string;
  messageId: string;
  /** `already_sent` on the 409 path — the page may tell the user; absent otherwise. */
  reason?: OutboxReason;
  /** Monotonic, so two deliveries of equal shape still read as two ticks. */
  seq: number;
}

/**
 * How long a delivered entry waits for the open thread page's `dropDelivered()`
 * before the store drops it on its own. A page that never acknowledges (an
 * instance Ionic kept alive under a different id, a watcher that lost the
 * race) must not leave a phantom "Sending…" bubble behind.
 */
const DELIVERED_ACK_TIMEOUT_MS = 8_000;

/**
 * When reconciling a "possibly delivered" entry against the thread, only a
 * message of ours sent no earlier than this before the entry was queued can
 * be its server copy — an identical "ok" from last week must not count.
 */
const RECONCILE_MATCH_SLACK_MS = 10 * 60_000;

export const useMessagesStore = defineStore('messages', () => {
  /** The REST list as fetched (direct threads + groups, newest first). */
  const fetched = ref<Conversation[]>([]);
  /** Per-thread state newer than the list, keyed by the unified thread identity. */
  const threadState = ref(new Map<ThreadKey, ThreadState>());
  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  /** The server's `unread_counts.messages` as of {@link serverAsOf} — informational, never the badge. */
  const serverUnread = ref(0);
  const serverAsOf = ref<string | null>(null);

  /**
   * The conversation list with {@link threadState} projected onto its rows.
   * Same shape and surface as before: the pages read `unread` / `updatedAt`
   * off these rows, and `storeToRefs()` hands out this computed.
   */
  const conversations = computed<Conversation[]>(() => {
    const state = threadState.value;
    if (!state.size) return fetched.value;
    return fetched.value.map((row) => {
      const held = state.get(threadKeyOfConversation(row));
      if (!held) return row;
      const unread = row.kind === 'direct' ? (held.unread > 0 ? 1 : 0) : Math.max(0, held.unread);
      const updatedAt = Math.max(row.updatedAt, held.updatedAt);
      return unread === row.unread && updatedAt === row.updatedAt ? row : { ...row, unread, updatedAt };
    });
  });

  /** Total unread across all conversations (projected) — drives the tab badge. */
  const unreadTotal = computed(() =>
    conversations.value.reduce((sum, c) => sum + (c.unread > 0 ? c.unread : 0), 0),
  );

  /** Counterpart user id → direct thread key(s), rebuilt from the fetched list. */
  const threadIndex = computed<ThreadIndex>(() => buildThreadIndex(fetched.value));

  /**
   * The thread page on screen, as that page named itself — the backstop for
   * everything {@link threadIndex} cannot translate (see {@link OpenThreadRef}).
   */
  const openThread = ref<OpenThreadRef | null>(null);

  /**
   * Register (or, with `null`, drop) the thread page on screen. A page does not
   * have to clear its own registration: one is honoured only while the live
   * route still matches it, so a stale one is inert.
   */
  function setOpenThread(open: OpenThreadRef | null): void {
    openThread.value = open;
  }

  let controller: AbortController | null = null;
  // The highest stream sequence applied; an older count is stale.
  let highestSequence: string | null = null;
  // notification id → thread, learned from the envelopes that carried both, so
  // a later `notification.read` of category `message` (which names only the
  // notification) can still find its thread. Insertion-ordered; oldest evicted.
  const notificationThreads = new Map<string, ThreadKey>();

  // Threads just opened, whose server-side read may not have committed before
  // the next list refresh lands. We re-assert the optimistic "read" once across
  // a refresh so the badge doesn't bounce back up, then forget it. Bounded to a
  // single refresh on purpose: read() is best-effort (and swallows failures), so
  // a longer-lived guard could permanently mask a thread that's truly unread.
  const pendingRead = new Set<string>();

  function advanceSequence(sequence: string | null | undefined): void {
    if (typeof sequence !== 'string' || !/^[0-9]{1,20}$/.test(sequence)) return;
    if (highestSequence === null || compareStreamSequence(sequence, highestSequence) > 0) {
      highestSequence = sequence;
    }
  }

  function rememberThread(notificationId: string | undefined, key: ThreadKey): void {
    if (!notificationId) return;
    notificationThreads.delete(notificationId);
    notificationThreads.set(notificationId, key);
    if (notificationThreads.size > NOTIFICATION_THREAD_MEMO_MAX) {
      const oldest = notificationThreads.keys().next().value;
      if (oldest !== undefined) notificationThreads.delete(oldest);
    }
  }

  /** The projected state of one thread (the row's own values when nothing newer is held). */
  function stateOf(key: ThreadKey): ThreadState | undefined {
    const held = threadState.value.get(key);
    if (held) return held;
    const id = conversationIdOf(key);
    const row = fetched.value.find((c) => c.id === id);
    return row ? { unread: row.unread, updatedAt: row.updatedAt } : undefined;
  }

  /** Current per-thread state to mutate — seeded from the row when nothing is held yet. */
  function holdThread(key: ThreadKey): ThreadState {
    const held = threadState.value.get(key);
    if (held) return held;
    const seed = stateOf(key) ?? { unread: 0, updatedAt: 0 };
    const fresh: ThreadState = { unread: seed.unread, updatedAt: seed.updatedAt };
    threadState.value.set(key, fresh);
    return fresh;
  }

  /**
   * Is the thread page for `key` the one on screen? A group is addressed by its
   * bare id; a direct thread by its root message id — or by any message id the
   * envelope names (a reply's id the page may still be showing before it
   * `router.replace`s to the root).
   *
   * Takes a RESOLVED key. The envelope-level entry point is
   * {@link isEnvelopeThreadOpen}, which the realtime service calls for its toast
   * decision — the service no longer keeps a copy of this rule.
   */
  function isThreadRouteOpen(key: ThreadKey, envelope?: RealtimeEnvelope): boolean {
    const current = router.currentRoute.value;
    const param = current.params.id;
    const currentId = Array.isArray(param) ? (param[0] ?? '') : (param ?? '');
    if (!currentId) return false;
    const { kind, id } = parseThreadKey(key);
    if (kind === 'group') return current.name === 'group-conversation' && currentId === id;
    if (current.name !== 'conversation') return false;
    if (currentId === id) return true;
    if (!envelope) return false;
    const candidates = [
      envelope.action?.params.message_id,
      envelope.action?.params.parent_message_id,
      envelope.resource?.type === 'message' ? envelope.resource.id : undefined,
    ];
    return candidates.some((candidate) => typeof candidate === 'string' && candidate.trim() === currentId);
  }

  /** Resolve the thread an envelope is about — its own action first, then the notification-id memory. */
  function resolveThread(envelope: RealtimeEnvelope): ThreadKey | 'unresolved' {
    const byAction = threadKeyOfEnvelope(envelope, threadIndex.value);
    if (byAction !== 'unresolved') return byAction;
    const notificationId = envelope.notification?.id;
    return (notificationId && notificationThreads.get(notificationId)) || 'unresolved';
  }

  /**
   * Is the thread this envelope is about the one on screen? THE check the toast
   * and the unread reducer share — `realtime.service.ts` calls this rather than
   * re-deriving, so a toast can never disagree with the count the same burst
   * just applied.
   *
   * It has to go through `resolveThread()`: on the wire a DIRECT thread is named
   * by the counterpart's USER id (or, on the sender's own copy, the root id),
   * while the app routes by the thread's ROOT MESSAGE id — and the envelope's
   * `message_id` / `resource.id` are the NEW message's id, which matches the
   * route only on a thread's very first message. Comparing those directly is
   * what made every direct-thread reply toast over the open conversation.
   *
   * Unresolvable (a counterpart the list does not hold, or holds twice) reads as
   * NOT open: the burst refreshes the list and a toast is the honest surface.
   */
  function isEnvelopeThreadOpen(envelope: RealtimeEnvelope): boolean {
    if (isOpenThreadEnvelope(envelope)) return true;
    const key = resolveThread(envelope);
    return key !== 'unresolved' && isThreadRouteOpen(key, envelope);
  }

  /**
   * The open page's own registration — the answer for the threads
   * {@link resolveThread} reads as `'unresolved'`, which is when the page was
   * being toasted over while it appended the message (see
   * {@link OpenThreadRef}). Honoured only while the live route is still the one
   * that registered it.
   */
  function isOpenThreadEnvelope(envelope: RealtimeEnvelope): boolean {
    const open = openThread.value;
    if (!open) return false;
    const current = router.currentRoute.value;
    const param = current.params.id;
    const currentId = Array.isArray(param) ? (param[0] ?? '') : (param ?? '');
    if (current.name !== open.routeName || currentId !== open.routeId) return false;
    return envelopeAddressesOpenThread(envelope, open);
  }

  /**
   * Adopt the server's message count for the record, then apply today's rule:
   * the count is never the badge, but a count that disagrees with the
   * projected total means the list is behind — refresh it. Ignored when older
   * than what this store has applied.
   */
  function setServerUnread(count: number, asOf: string | null): void {
    if (asOf !== null) {
      if (!/^[0-9]{1,20}$/.test(asOf)) return;
      if (highestSequence !== null && compareStreamSequence(asOf, highestSequence) < 0) return;
      if (serverAsOf.value !== null && compareStreamSequence(asOf, serverAsOf.value) < 0) return;
      advanceSequence(asOf);
    }
    serverUnread.value = Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0;
    serverAsOf.value = asOf ?? highestSequence ?? serverAsOf.value;
    if (serverUnread.value !== unreadTotal.value) void refresh();
  }

  /**
   * Reduce one accepted stream envelope (live or drained).
   *
   *   `message.created`   — resolve the thread; a sender-sync copy (the actor is
   *                         this session's user) only moves `updatedAt`; else a
   *                         direct thread becomes unread (boolean), a group
   *                         counts one more — unless that thread's page is open.
   *   `notification.read` of category `message`, or any READ whose action is
   *                         `messages.thread` — the thread is read. A thread
   *                         read on the server converges its bell items to
   *                         exactly this event (Messages_model →
   *                         mark_read_for_resource), which is the path that
   *                         keeps the two badges in step (DEC-028).
   *   `notification.dismissed` of category `message` — IGNORED for thread
   *                         state (DEC-098): dismissing a bell item never marks
   *                         the message row read server-side, so zeroing the
   *                         thread here would only flap back to unread on the
   *                         next list refresh. The notifications store still
   *                         applies the dismissal to the bell item.
   *   `message.read`      — parse-valid, no emitter: `'unresolved'` so the
   *                         service reconciles if it is ever seen.
   *
   * `'unresolved'` (a thread the list does not hold, a counterpart with several
   * roots, a read whose notification this store never saw) asks the service
   * for ONE list refresh per burst.
   */
  function applyEvent(envelope: RealtimeEnvelope): ReducerOutcome {
    advanceSequence(envelope.sequence);

    if (envelope.type === 'message.created') {
      const key = resolveThread(envelope);
      if (key === 'unresolved') return 'unresolved';
      rememberThread(envelope.notification?.id, key);
      const state = holdThread(key);
      const occurredAt = toMillis(envelope.occurred_at);
      if (occurredAt > state.updatedAt) state.updatedAt = occurredAt;
      const senderSync = isSelfActor(envelope);
      if (senderSync) return 'applied';
      if (isThreadRouteOpen(key, envelope)) {
        state.unread = 0;
        return 'applied';
      }
      state.unread = key.startsWith('group:') ? state.unread + 1 : 1;
      return 'applied';
    }

    if (envelope.type === 'notification.created') {
      // Only the thread memory: the bell item itself belongs to the
      // notifications store. A message notification's `created` carries the
      // thread action, so a later read of it can be resolved.
      if (envelope.notification?.category !== 'message') return 'ignored';
      const key = threadKeyOfEnvelope(envelope, threadIndex.value);
      if (key !== 'unresolved') rememberThread(envelope.notification.id, key);
      return 'ignored';
    }

    if (STATE_TYPES.has(envelope.type)) {
      const isMessage =
        envelope.notification?.category === 'message' || envelope.action?.route === 'messages.thread';
      if (!isMessage) return 'ignored';
      // A dismissal only clears the bell item; the message row stays unread
      // on the server, so the thread badge must not move (DEC-098).
      if (envelope.type === 'notification.dismissed') return 'ignored';
      const key = resolveThread(envelope);
      if (key === 'unresolved') return 'unresolved';
      const state = holdThread(key);
      if (state.unread !== 0) state.unread = 0;
      return 'applied';
    }

    // `message.read`: forward-compatibility only — nothing emits it today.
    return 'unresolved';
  }

  /** (Re)load the conversation list. Concurrent calls cancel the older fetch. */
  async function refresh(): Promise<void> {
    controller?.abort();
    controller = new AbortController();
    const { signal } = controller;

    loading.value = true;
    error.value = null;
    try {
      // Direct threads (`/me/messages`) and group chats (`/me/message-groups`)
      // are separate endpoints — fetch in parallel and merge into one list.
      // One failing shouldn't blank the other; only surface an error if BOTH do.
      const [direct, groups] = await Promise.allSettled([
        messagesService.list(undefined, signal),
        messageGroupsService.list(undefined, signal),
      ]);
      if (signal.aborted) return;
      if (direct.status === 'rejected' && groups.status === 'rejected') throw direct.reason;
      let merged = [
        ...(direct.status === 'fulfilled' ? direct.value : []),
        ...(groups.status === 'fulfilled' ? groups.value : []),
      ].sort((a, b) => b.updatedAt - a.updatedAt);
      // Re-assert just-opened threads whose read may still be in flight, then
      // forget them (one-shot) so the server stays the source of truth after.
      if (pendingRead.size) {
        merged = merged.map((c) => (pendingRead.has(c.id) && c.unread !== 0 ? { ...c, unread: 0 } : c));
        pendingRead.clear();
      }
      // The list replaces everything the reducer accumulated: it is the
      // server's state as of now, and it is re-projected from scratch.
      fetched.value = merged;
      threadState.value = new Map();
    } catch (err) {
      if (signal.aborted || (err instanceof DOMException && err.name === 'AbortError')) return;
      error.value = errorMessage(err);
    } finally {
      if (!signal.aborted) {
        loading.value = false;
        loaded.value = true;
      }
    }
  }

  /** Optimistically clear a conversation's unread count (e.g. on opening it). */
  function markConversationRead(id: string): void {
    // Track it even if it isn't in the list yet (cold deep-link into a thread):
    // the next refresh will re-apply the read once the list arrives.
    pendingRead.add(id);
    const kind = id.startsWith('g-') ? 'group' : 'direct';
    const key = threadKeyOfConversation({ id, kind });
    const current = stateOf(key);
    if (!current || current.unread === 0) return;
    holdThread(key).unread = 0;
  }

  /** Clear all state — call on sign-out so the next user starts clean. */
  function reset(): void {
    controller?.abort();
    controller = null;
    fetched.value = [];
    threadState.value = new Map();
    loading.value = false;
    loaded.value = false;
    error.value = null;
    serverUnread.value = 0;
    serverAsOf.value = null;
    highestSequence = null;
    notificationThreads.clear();
    pendingRead.clear();
    openThread.value = null;
    resetOutboxMemory();
  }

  // ── Outbox (W3) — phase6-contract.md §3.4 ────────────────────────────────
  //
  // The store is the SINGLE owner of the queue. Nothing above this line sends
  // a message; the thread pages read `useMessageOutbox(scope)` (a computed view
  // over `outbox`) and call `enqueueOutbox()` / `deliver()` / `retryOutbox()` /
  // `discardOutbox()`; the realtime service calls `flushOutbox()` on
  // `connected` and on the reachability edge; `signOutLocal()` calls
  // `clearOutboxForSession()` with the session identity snapshot.

  /** Every scope this session has touched, keyed by the v2 storage key; arrays are replaced, never mutated. */
  const outbox = ref(new Map<OutboxScopeKey, OutboxMessage[]>());
  /** Scopes with a request in flight — memory only; one in flight per scope. */
  const inFlight = new Set<OutboxScopeKey>();
  /** The last delivery (see {@link OutboxDeliveredTick}). */
  const delivered = ref<OutboxDeliveredTick | null>(null);
  let deliveredSeq = 0;
  /** Delivered entries waiting for the open page's `dropDelivered()`, bounded by {@link DELIVERED_ACK_TIMEOUT_MS}. */
  const ackTimers = new Map<string, ReturnType<typeof setTimeout>>();
  let lastFlushAt = 0;

  const ackHandle = (scope: OutboxScopeKey, localId: string): string => `${scope}|${localId}`;

  function clearAck(scope: OutboxScopeKey, localId: string): void {
    const handle = ackHandle(scope, localId);
    const timer = ackTimers.get(handle);
    if (timer !== undefined) {
      clearTimeout(timer);
      ackTimers.delete(handle);
    }
  }

  function resetOutboxMemory(): void {
    for (const timer of ackTimers.values()) clearTimeout(timer);
    ackTimers.clear();
    outbox.value = new Map();
    inFlight.clear();
    delivered.value = null;
    lastFlushAt = 0;
  }

  /** Read a scope from storage the first time it is named (v1 keys are deleted on the way). */
  function hydrateOutbox(scope: OutboxScopeKey): void {
    if (!outbox.value.has(scope)) outbox.value.set(scope, loadOutbox(scope));
  }

  /** The queue of one scope, oldest first (empty until hydrated). */
  function outboxEntries(scope: OutboxScopeKey): OutboxMessage[] {
    return outbox.value.get(scope) ?? [];
  }

  function writeOutbox(scope: OutboxScopeKey, entries: OutboxMessage[]): void {
    outbox.value.set(scope, entries);
    persistOutbox(scope, entries);
  }

  function patchEntry(
    scope: OutboxScopeKey,
    localId: string,
    patch: Partial<Pick<OutboxMessage, 'status' | 'attempts'>> & { reason?: OutboxReason | null },
  ): OutboxMessage | undefined {
    let updated: OutboxMessage | undefined;
    const next = outboxEntries(scope).map((m) => {
      if (m.localId !== localId) return m;
      const { reason, ...rest } = patch;
      updated = { ...m, ...rest };
      if (reason === null) delete updated.reason;
      else if (reason !== undefined) updated.reason = reason;
      return updated;
    });
    if (updated) writeOutbox(scope, next);
    return updated;
  }

  function removeEntry(scope: OutboxScopeKey, localId: string): void {
    clearAck(scope, localId);
    const entries = outboxEntries(scope);
    if (!entries.some((m) => m.localId === localId)) return;
    writeOutbox(
      scope,
      entries.filter((m) => m.localId !== localId),
    );
  }

  /**
   * Queue a message for `threadKey` under the CURRENT session identity.
   * Throws `OutboxScopeError` when the identity is not known (either field
   * ''), and `OutboxFullError` at `OUTBOX_MAX_ENTRIES_PER_SCOPE` — the enqueue
   * is refused; nothing already queued is dropped. The entry carries its
   * `clientEventId` (UUID v4) and `requestHash` from here on; it does NOT send
   * — call {@link deliver} with the returned scope + entry.
   */
  async function enqueueOutbox(
    threadKey: ThreadKey,
    body: string,
    attachments: OutboxAttachment[] = [],
  ): Promise<{ scope: OutboxScopeKey; entry: OutboxMessage }> {
    const scope = outboxScopeKey(getSessionScope(), threadKey);
    hydrateOutbox(scope);
    if (outboxEntries(scope).length >= OUTBOX_MAX_ENTRIES_PER_SCOPE) throw new OutboxFullError();
    const tokens = attachments.map((a) => a.token).filter(Boolean);
    const requestHash = await computeRequestHash(threadKey, body, tokens);
    // The hash awaited; re-check the bound against whatever landed meanwhile.
    if (outboxEntries(scope).length >= OUTBOX_MAX_ENTRIES_PER_SCOPE) throw new OutboxFullError();
    const entry = createOutboxEntry({ body, attachments, requestHash });
    writeOutbox(scope, [...outboxEntries(scope), entry]);
    return { scope, entry };
  }

  /**
   * Send one queued entry with ITS `clientEventId` (never a fresh one).
   *
   *   `replayed: true` / 201     → delivered.
   *   409 `idempotency_conflict` → the key is persisted, i.e. delivered
   *                                (DEC-093): the page refreshes, the entry is
   *                                removed; it is never re-sent under a new id.
   *   422 naming attachments /   → `attempts > 1`: possibly delivered — the
   *   a token                      thread is re-read; a message from self with
   *                                the same normalised text is its server copy
   *                                (delivered), else a terminal `conflict`
   *                                (`stale_attachments`); a FIRST attempt is a
   *                                plain rejection.
   *   network (status 0), 429,   → `failed` (retryable by the flush).
   *   5xx
   *   other 4xx                  → `failed` with `reason: 'rejected'` (manual
   *                                retry only).
   *   403 `recipient_unavailable`→ terminal `conflict` (`recipient_unavailable`):
   *                                the other party has left the company and the
   *                                thread is read-only — never re-sent, never
   *                                "delivered"; the bubble offers Discard only.
   *
   * `recipient_unavailable` is classified FIRST, by code: were it ever answered
   * with a 409 it must still not read as "delivered" (isAlreadySentError()).
   *
   * `attempts` is incremented and persisted BEFORE the request goes out, so a
   * reload mid-flight still counts it. A result landing after a sign-out is
   * discarded (session epoch). One request per scope at a time.
   */
  async function deliver(scope: OutboxScopeKey, entry: Pick<OutboxMessage, 'localId'>): Promise<void> {
    if (inFlight.has(scope)) return;
    const threadKey = threadKeyOfScope(scope);
    if (!threadKey) return;
    hydrateOutbox(scope);
    const current = outboxEntries(scope).find((m) => m.localId === entry.localId);
    if (!current || current.status === 'conflict') return;

    const epoch = getSessionEpoch();
    inFlight.add(scope);
    try {
      const attempt = patchEntry(scope, current.localId, {
        status: 'sending',
        reason: null,
        attempts: current.attempts + 1,
      });
      if (!attempt) return;
      const { kind, id } = parseThreadKey(threadKey);
      const tokens = attempt.attachments.map((a) => a.token).filter(Boolean);
      const staged = tokens.length ? tokens : undefined;
      let messageId: string;
      try {
        const result =
          kind === 'group'
            ? await messageGroupsService.send(id, attempt.body, staged, attempt.clientEventId)
            : await messagesService.reply(id, attempt.body, staged, attempt.clientEventId);
        messageId = result.messageId;
      } catch (err) {
        if (getSessionEpoch() !== epoch) return;
        await settleFailure(scope, attempt, tokens, err, epoch);
        return;
      }
      if (getSessionEpoch() !== epoch) return;
      settleDelivered(scope, attempt.localId, messageId);
    } finally {
      inFlight.delete(scope);
    }
  }

  async function settleFailure(
    scope: OutboxScopeKey,
    entry: OutboxMessage,
    tokens: string[],
    err: unknown,
    epoch: number,
  ): Promise<void> {
    if (isRecipientUnavailableError(err)) {
      // Terminal: deliver() skips `conflict`, flushScope()/retryOutbox() pick
      // only `failed` — a refused message costs exactly one request.
      patchEntry(scope, entry.localId, { status: 'conflict', reason: 'recipient_unavailable' });
      return;
    }
    if (isAlreadySentError(err)) {
      settleDelivered(scope, entry.localId, '', 'already_sent');
      return;
    }
    if (entry.attachments.length && isStaleAttachmentsError(err, tokens)) {
      if (entry.attempts > 1) {
        await reconcilePossiblyDelivered(scope, entry, epoch);
        return;
      }
      patchEntry(scope, entry.localId, { status: 'failed', reason: 'rejected' });
      return;
    }
    if (isRejectedError(err)) {
      patchEntry(scope, entry.localId, { status: 'failed', reason: 'rejected' });
      return;
    }
    // Network, 429, 5xx, or something we cannot classify: retryable.
    patchEntry(scope, entry.localId, { status: 'failed', reason: null });
  }

  /** The "possibly delivered" branch: re-read the thread and look for our own copy. */
  async function reconcilePossiblyDelivered(scope: OutboxScopeKey, entry: OutboxMessage, epoch: number): Promise<void> {
    const threadKey = threadKeyOfScope(scope);
    if (!threadKey) return;
    const { kind, id } = parseThreadKey(threadKey);
    const meId = getSessionScope().userId;
    let thread: Conversation;
    try {
      thread =
        kind === 'group'
          ? await messageGroupsService.thread(id, undefined, undefined, meId)
          : await messagesService.thread(id, undefined, undefined, meId);
    } catch {
      if (getSessionEpoch() !== epoch) return;
      // Could not look; leave it retryable so the next flush asks again.
      patchEntry(scope, entry.localId, { status: 'failed', reason: null });
      return;
    }
    if (getSessionEpoch() !== epoch) return;
    const copy = findOwnCopy(thread.messages, entry);
    if (copy) settleDelivered(scope, entry.localId, copy.id);
    else patchEntry(scope, entry.localId, { status: 'conflict', reason: 'stale_attachments' });
  }

  /**
   * Our server-side copy of a queued entry, if the thread holds one: a message
   * from self, sent no earlier than the entry was queued (with slack), whose
   * normalised text equals the entry's — or, for an attachment-only entry, one
   * with an empty body and the same attachment names. Newest match wins.
   */
  function findOwnCopy(messages: readonly ChatMessage[], entry: OutboxMessage): ChatMessage | undefined {
    const wanted = normalizeMessageText(entry.body);
    const wantedNames = entry.attachments.map((a) => a.name).sort();
    const since = entry.createdAt - RECONCILE_MATCH_SLACK_MS;
    let found: ChatMessage | undefined;
    for (const message of messages) {
      if (message.authorId !== ME) continue;
      if (message.sentAt > 0 && message.sentAt < since) continue;
      if (normalizeMessageText(message.body) !== wanted) continue;
      if (wanted === '') {
        const names = (message.attachments ?? []).map((a) => a.name).sort();
        if (names.length !== wantedNames.length || names.some((n, i) => n !== wantedNames[i])) continue;
      }
      if (!found || message.sentAt >= found.sentAt) found = message;
    }
    return found;
  }

  /**
   * Announce a delivery. While that thread's page is open the entry stays
   * until the page has refreshed and called {@link dropDelivered} (bounded by
   * {@link DELIVERED_ACK_TIMEOUT_MS}); otherwise it drops at once.
   */
  function settleDelivered(scope: OutboxScopeKey, localId: string, messageId: string, reason?: OutboxReason): void {
    const tick: OutboxDeliveredTick = { scope, localId, messageId, seq: ++deliveredSeq };
    if (reason) tick.reason = reason;
    delivered.value = tick;
    const threadKey = threadKeyOfScope(scope);
    if (threadKey && isThreadRouteOpen(threadKey)) {
      clearAck(scope, localId);
      ackTimers.set(
        ackHandle(scope, localId),
        setTimeout(() => {
          ackTimers.delete(ackHandle(scope, localId));
          removeEntry(scope, localId);
        }, DELIVERED_ACK_TIMEOUT_MS),
      );
      return;
    }
    removeEntry(scope, localId);
  }

  /** The open page's acknowledgement of a `delivered` tick: its thread is refreshed, drop the bubble. */
  function dropDelivered(scope: OutboxScopeKey, localId: string): void {
    removeEntry(scope, localId);
  }

  /** Manual retry from a `failed` bubble (any reason — the user asked). */
  function retryOutbox(scope: OutboxScopeKey, localId: string): void {
    const entry = outboxEntries(scope).find((m) => m.localId === localId);
    if (!entry || entry.status !== 'failed') return;
    void deliver(scope, entry);
  }

  /** Discard an entry (a `conflict` bubble's Discard, or Edit & resend after the text moved to the composer). */
  function discardOutbox(scope: OutboxScopeKey, localId: string): void {
    removeEntry(scope, localId);
  }

  /** Deliver the oldest retryable `failed` entry of one scope (one in flight per scope). */
  function flushScope(scope: OutboxScopeKey): void {
    hydrateOutbox(scope);
    if (inFlight.has(scope)) return;
    const next = outboxEntries(scope)
      .filter((m) => m.status === 'failed' && m.reason !== 'rejected')
      .sort((a, b) => a.createdAt - b.createdAt)[0];
    if (next) void deliver(scope, next);
  }

  /**
   * `flushOutbox()` — every scope of the current session, at most once per
   * `OUTBOX_FLUSH_MIN_INTERVAL_MS` (the realtime service's `connected` and
   * reachability-edge trigger). `flushOutbox(scope)` — that one scope, now,
   * un-throttled (a thread page on open). `conflict` and `rejected` entries are
   * skipped either way.
   */
  function flushOutbox(scope?: OutboxScopeKey): void {
    if (scope) {
      flushScope(scope);
      return;
    }
    const now = Date.now();
    if (now - lastFlushAt < OUTBOX_FLUSH_MIN_INTERVAL_MS) return;
    lastFlushAt = now;
    const session = getSessionScope();
    const prefix = outboxSessionPrefix(session);
    if (prefix === null) return;
    // Storage is the list of record; a scope held in memory whose persist
    // failed (quota, private mode) is still walked.
    const scopes = new Set<OutboxScopeKey>(listOutboxScopeKeys(session));
    for (const key of outbox.value.keys()) {
      if (key.startsWith(prefix)) scopes.add(key);
    }
    for (const key of scopes) flushScope(key);
  }

  /**
   * Remove every `syntry.msg_outbox.v2:<companyKey>:<userId>:*` key and every
   * v1 key from storage (an empty snapshot sweeps the whole prefix), and forget
   * every queue held in memory. Called from `signOutLocal()` with the session
   * identity SNAPSHOT (§3.1) — a company switch is a sign-out.
   */
  function clearOutboxForSession(session: SessionScope): void {
    clearOutboxStorage(session);
    resetOutboxMemory();
  }

  return {
    conversations,
    threadState,
    loading,
    loaded,
    error,
    unreadTotal,
    serverUnread,
    serverAsOf,
    stateOf,
    setServerUnread,
    applyEvent,
    isEnvelopeThreadOpen,
    setOpenThread,
    refresh,
    markConversationRead,
    reset,
    // outbox (§3.4)
    outbox,
    delivered,
    hydrateOutbox,
    outboxEntries,
    enqueueOutbox,
    deliver,
    retryOutbox,
    discardOutbox,
    dropDelivered,
    flushOutbox,
    clearOutboxForSession,
  };
});
